import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import {
  MapPin, CheckCircle,
  Mail, Phone, Sparkles, Building, ArrowLeft, Calendar,
  ExternalLink, Heart, MessageCircle, Shield
} from "lucide-react";
import CLPBanner from "../components/CLPBanner";
import CLPEngagementModal from "../components/CLPEngagementModal";
import CLPTourInterceptModal from "../components/CLPTourInterceptModal";
import FeaturedDeals from "../components/FeaturedDeals";
import { formatSpecial, startingAround, PRICING_DISCLAIMER } from "../lib/formatSpecial";

const InstantResultsPage = () => {
  const location = useLocation();
  const { apartments = [], searchCriteria = {}, leadId = null, emailSent = false } = location.state || {};

  // CLP State
  const [viewCount, setViewCount] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [showTourIntercept, setShowTourIntercept] = useState(false);
  const [interceptUrl, setInterceptUrl] = useState("");
  const [engagementShown, setEngagementShown] = useState(false);
  const viewedApts = useRef(new Set());

  // Track a property view
  const trackView = useCallback(async (aptId) => {
    if (!leadId || viewedApts.current.has(aptId)) return;
    viewedApts.current.add(aptId);
    const newCount = viewCount + 1;
    setViewCount(newCount);

    try {
      await axios.post(`${API}/clp/track`, {
        lead_id: leadId,
        event_type: newCount > 1 ? "repeat_view" : "property_view",
        deal_id: aptId,
      });
    } catch (e) {
      console.error("Track view failed:", e);
    }

    // Trigger engagement modal after 3 views
    if (newCount >= 3 && !engagementShown) {
      setEngagementShown(true);
      setShowEngagementModal(true);
    }
  }, [leadId, viewCount, engagementShown]);

  // Track card visibility using IntersectionObserver
  useEffect(() => {
    if (!leadId || !apartments.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const aptId = entry.target.dataset.aptId;
            if (aptId) trackView(aptId);
          }
        });
      },
      { threshold: 0.5 }
    );

    const cards = document.querySelectorAll("[data-apt-id]");
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [leadId, apartments, trackView]);

  // Toggle favorite
  const toggleFavorite = async (aptId) => {
    if (!leadId) return;
    const isFav = favorites.includes(aptId);

    // Optimistic update
    setFavorites((prev) =>
      isFav ? prev.filter((id) => id !== aptId) : [...prev, aptId]
    );

    try {
      await axios.post(`${API}/clp/favorite`, {
        lead_id: leadId,
        deal_id: aptId,
      });
    } catch {
      // Revert on error
      setFavorites((prev) =>
        isFav ? [...prev, aptId] : prev.filter((id) => id !== aptId)
      );
    }
  };

  // Handle external link click (intercept for CLP)
  const handleExternalClick = (url, aptId) => {
    if (leadId) {
      // Track tour click
      axios.post(`${API}/clp/track`, {
        lead_id: leadId,
        event_type: "tour_click",
        deal_id: aptId,
      }).catch(() => {});
    }
    setInterceptUrl(url);
    setShowTourIntercept(true);
  };

  // Handle "Schedule Tour" / Calendly click
  const handleScheduleTour = () => {
    if (leadId) {
      axios.post(`${API}/clp/track`, {
        lead_id: leadId,
        event_type: "tour_click",
      }).catch(() => {});
    }
    if (!engagementShown) {
      setEngagementShown(true);
      setShowEngagementModal(true);
    } else {
      setInterceptUrl("/book");
      setShowTourIntercept(true);
    }
  };

  if (!apartments.length && !location.state) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#0A2342] mb-2">No Results Found</h2>
          <p className="text-slate-600 mb-6">Please start a new search from the homepage.</p>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      {/* CLP Banner */}
      <CLPBanner />

      {/* Success Header */}
      <section className="bg-[#0A2342] py-12">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Playfair_Display']" data-testid="results-title">
              We Found {apartments.length} Matches For You!
            </h1>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Based on your search: <span className="text-[#DE6E4B] font-medium">{searchCriteria.bedrooms}</span> in{" "}
              <span className="text-[#DE6E4B] font-medium">{searchCriteria.area}</span>,{" "}
              <span className="text-[#DE6E4B] font-medium">${searchCriteria.budget_min?.toLocaleString()} - ${searchCriteria.budget_max?.toLocaleString()}/mo</span>
            </p>
            {emailSent && (
              <div className="mt-4 inline-flex items-center px-4 py-2 bg-green-500/20 rounded-full text-green-300 text-sm">
                <Mail className="w-4 h-4 mr-2" /> Full list sent to your email!
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Results Grid */}
      <section className="py-12">
        <div className="container-main">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apartments.map((apt, index) => {
              const isFav = favorites.includes(apt.id);
              return (
                <motion.div
                  key={apt.id}
                  data-apt-id={apt.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all group relative"
                  data-testid={`apartment-result-${index}`}
                >
                  {/* Favorite Button */}
                  {leadId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(apt.id);
                      }}
                      className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                        isFav
                          ? "bg-red-500 text-white shadow-lg shadow-red-500/30"
                          : "bg-white/90 text-slate-400 hover:text-red-500 shadow-md"
                      }`}
                      data-testid={`favorite-btn-${index}`}
                      aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
                    >
                      <Heart className={`w-4 h-4 ${isFav ? "fill-white" : ""}`} />
                    </button>
                  )}

                  {apt.special_offer && (
                    <div className="bg-[#DE6E4B] px-4 py-2">
                      <div className="flex items-center text-white text-sm font-medium">
                        <Sparkles className="w-4 h-4 mr-2 shrink-0" />
                        <span>
                          <span className="font-bold tracking-wide">{formatSpecial(apt.special_offer)?.headline}</span>
                          {formatSpecial(apt.special_offer)?.detail && <span className="block text-white/80 text-xs font-normal">{formatSpecial(apt.special_offer).detail}</span>}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#0A2342] mb-2 font-['Playfair_Display'] group-hover:text-[#DE6E4B] transition-colors pr-10">
                      {apt.name}
                    </h3>
                    <div className="flex items-center text-slate-500 text-sm mb-4">
                      <MapPin className="w-4 h-4 mr-1" /> {apt.location}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="badge bg-[#0A2342] text-white">{startingAround(apt.price_min)}</span>
                      <span className="badge bg-slate-100 text-slate-700">{apt.bedrooms}</span>
                    </div>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{apt.description}</p>
                    {apt.amenities?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-slate-500 mb-2">Top Amenities:</p>
                        <div className="flex flex-wrap gap-1">
                          {apt.amenities.slice(0, 4).map((amenity) => (
                            <span key={amenity} className="text-xs bg-slate-50 text-slate-600 px-2 py-1 rounded">{amenity}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="pt-4 border-t border-slate-100 space-y-2">
                      {apt.source_link && (
                        <button
                          onClick={() => handleExternalClick(apt.source_link, apt.id)}
                          className="w-full py-2.5 bg-[#DE6E4B] text-white rounded-lg text-sm font-medium hover:bg-[#c5593d] transition-colors flex items-center justify-center gap-2"
                          data-testid={`view-listing-btn-${index}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> View Full Listing
                        </button>
                      )}
                      <a
                        href={`sms:+19362241484?body=Hi%20Bryan%2C%20I%27m%20interested%20in%20${encodeURIComponent(apt.name || 'a listing')}!`}
                        className="w-full py-2 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-1.5"
                        data-testid={`text-bryan-btn-${index}`}
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> Text Bryan About This
                      </a>
                    </div>

                    {/* Reminder text below action */}
                    <p className="text-[10px] text-slate-400 mt-2 text-center leading-tight">
                      <Shield className="w-3 h-3 inline mr-0.5 -mt-0.5" />
                      List <span className="font-medium">Bryan Spann</span> on guest cards when touring
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <p className="mt-4 text-center text-xs text-slate-500" data-testid="pricing-disclaimer">{PRICING_DISCLAIMER}</p>
        </div>
      </section>

      {/* Featured Deals Section */}
      <FeaturedDeals
        title="More Deals You Might Like"
        subtitle="Top-scoring apartments from our deal inventory matching your area."
        budgetMin={searchCriteria.budget_min}
        budgetMax={searchCriteria.budget_max}
        area={searchCriteria.area}
        bedrooms={searchCriteria.bedrooms}
        limit={3}
        cta="apply"
        bg="bg-white"
      />

      {/* CTA Section */}
      <section className="bg-white py-16">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="bg-[#0A2342] rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Playfair_Display']">
              Want More Options & Exclusive Deals?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              These are just the beginning! I have access to dozens more apartments with exclusive move-in specials not shown here.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleScheduleTour}
                className="btn-primary bg-[#DE6E4B] hover:bg-white hover:text-[#0A2342] flex items-center justify-center"
                data-testid="cta-book-tour"
              >
                <Calendar className="w-5 h-5 mr-2" /> Book Free Consultation
              </button>
              <a href="tel:+19362241484"
                className="btn-secondary border-white text-white hover:bg-white hover:text-[#0A2342] flex items-center justify-center" data-testid="cta-call">
                <Phone className="w-5 h-5 mr-2" /> Call (936) 224-1484
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back Link */}
      <div className="container-main pb-12">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-[#0A2342] transition-colors" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Link>
      </div>

      {/* CLP Modals */}
      <CLPEngagementModal
        open={showEngagementModal}
        onClose={() => setShowEngagementModal(false)}
        leadId={leadId}
      />
      <CLPTourInterceptModal
        open={showTourIntercept}
        onClose={() => {
          setShowTourIntercept(false);
          setInterceptUrl("");
        }}
        leadId={leadId}
        externalUrl={interceptUrl}
      />
    </div>
  );
};

export default InstantResultsPage;
